/**
 * ProfessionalForm — componente CANÔNICO de formulário de profissional (P2.2).
 *
 * Um único form para create e edit de staff. Consumidores previstos:
 *   - TeamPage (create, dentro do modal "Adicionar profissional")
 *   - StaffDetailPage (edit, seção "Dados")
 *   - ClubSetupWizardPage (segunda passada, após estabilização)
 *
 * Regras de design:
 *   - SEM chamadas HTTP aqui. O componente cuida apenas do formulário;
 *     quem consome decide o endpoint (POST/PUT /roster/staff...).
 *   - Contrato de campos: SEMPRE staff_role (nunca 'role' — ver P1.1 hotfix:
 *     backend descartava 'role' e o INSERT falhava por NOT NULL + CHECK).
 *   - Campos oficiais de club_staff via sp_save_club_staff:
 *     full_name*, email, phone, staff_role*, hire_date.
 *   - Visual preto/branco/cinza (padrão G2-UX.1): cor só com significado.
 *
 * Interface:
 *   <ProfessionalForm
 *     mode="create" | "edit"
 *     initialValues={{ full_name, email, phone, staff_role, hire_date }}
 *     onSubmit={(values) => Promise|void}   // recebe payload canônico
 *     onCancel={() => void}                 // opcional
 *     loading={bool}                        // estado externo de saving
 *     error={string|null}                   // erro externo (ex.: resposta API)
 *   />
 */
import React, { useState, useEffect } from 'react';

const INK = '#111827';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';

export const ROLE_LABELS = {
  TREINADOR: 'Treinador',
  PREPARADOR_FISICO: 'Prep. Físico',
  NUTRICIONISTA: 'Nutricionista',
  FISIOTERAPEUTA: 'Fisioterapeuta',
  MEDICO: 'Médico',
  ANALISTA: 'Analista',
  AUXILIAR: 'Auxiliar',
  GESTOR: 'Gestor',
};

/** Funções selecionáveis no cadastro (GESTOR/AUXILIAR ficam fora do dropdown,
 *  como já era na TeamPage — GESTOR nasce por outro fluxo). */
export const SELECTABLE_ROLES = ['TREINADOR', 'PREPARADOR_FISICO',
  'NUTRICIONISTA', 'FISIOTERAPEUTA', 'MEDICO', 'ANALISTA'];

const EMPTY = { full_name: '', email: '', phone: '', staff_role: 'TREINADOR', hire_date: '' };

/** Normaliza initialValues (edit): null → '', hire_date ISO → yyyy-mm-dd. */
const normalize = (v = {}) => ({
  full_name: v.full_name || '',
  email: v.email || '',
  phone: v.phone || '',
  staff_role: v.staff_role || 'TREINADOR',
  hire_date: v.hire_date ? String(v.hire_date).slice(0, 10) : '',
});

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-bold uppercase mb-1" style={{ color: MUTED }}>
      {label}
    </label>
    {children}
  </div>
);

const inputCls = 'w-full px-3 py-2 border rounded-lg text-sm';
const inputStyle = { borderColor: BORDER, color: INK };

const ProfessionalForm = ({
  mode = 'create',
  initialValues = null,
  onSubmit,
  onCancel,
  loading = false,
  error = null,
}) => {
  const [form, setForm] = useState(() => initialValues ? normalize(initialValues) : EMPTY);
  // INV-4 (create): convite de acesso no ato do cadastro — marcado por padrão.
  // O backend orquestra (staff commitado → convite → e-mail best-effort).
  const [sendInvite, setSendInvite] = useState(true);

  // Edit: re-sincroniza quando a ficha carrega/muda
  useEffect(() => {
    if (initialValues) setForm(normalize(initialValues));
  }, [initialValues]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const inviteRequested = mode === 'create' && sendInvite;
  // Convite exige e-mail — sem e-mail o backend não teria destino de identidade.
  const valid = form.full_name.trim().length > 0 && !!form.staff_role
    && (!inviteRequested || form.email.trim().length > 0);

  const submit = (e) => {
    e?.preventDefault?.();
    if (!valid || loading) return;
    // Payload canônico: strings vazias viram null (opcional no sp_save_club_staff)
    onSubmit({
      full_name: form.full_name.trim(),
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      staff_role: form.staff_role,
      hire_date: form.hire_date || null,
      ...(mode === 'create' ? { send_invite: inviteRequested } : {}),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label="Nome completo *">
        <input className={inputCls} style={inputStyle}
          value={form.full_name} onChange={set('full_name')} autoFocus={mode === 'create'} />
      </Field>
      <Field label="E-mail">
        <input type="email" className={inputCls} style={inputStyle}
          value={form.email} onChange={set('email')} />
      </Field>
      <Field label="Telefone">
        <input type="tel" className={inputCls} style={inputStyle}
          value={form.phone} onChange={set('phone')} placeholder="(00) 00000-0000" />
      </Field>
      <Field label="Função *">
        <select className={`${inputCls} bg-white`} style={inputStyle}
          value={form.staff_role} onChange={set('staff_role')}>
          {SELECTABLE_ROLES.map((r) => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
          {/* Edit: preserva função fora do dropdown padrão (ex.: GESTOR legado) */}
          {!SELECTABLE_ROLES.includes(form.staff_role) && (
            <option value={form.staff_role}>
              {ROLE_LABELS[form.staff_role] || form.staff_role}
            </option>
          )}
        </select>
      </Field>
      <Field label="Data de contratação">
        <input type="date" className={inputCls} style={inputStyle}
          value={form.hire_date} onChange={set('hire_date')} />
      </Field>

      {mode === 'create' && (
        <div className="border rounded-lg p-3" style={{ borderColor: BORDER }}>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={sendInvite} className="mt-0.5"
              onChange={(e) => setSendInvite(e.target.checked)} />
            <span>
              <span className="block text-sm font-bold" style={{ color: INK }}>
                Enviar convite de acesso por e-mail
              </span>
              <span className="block text-xs mt-0.5" style={{ color: MUTED }}>
                O profissional recebe um link para criar ou vincular sua conta
                na VUKZ AI.
              </span>
            </span>
          </label>
          {inviteRequested && !form.email.trim() && (
            <p className="text-xs mt-2" style={{ color: '#b91c1c' }}>
              Informe o e-mail para enviar o convite.
            </p>
          )}
        </div>
      )}

      {error && <p className="text-sm" style={{ color: '#b91c1c' }}>{error}</p>}

      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={loading || !valid}
          className="flex-1 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
          style={{ backgroundColor: INK }}>
          {loading ? 'Salvando…' : (mode === 'create' ? 'Adicionar' : 'Salvar alterações')}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel}
            className="px-4 py-2 border rounded-lg text-sm font-semibold"
            style={{ borderColor: BORDER, color: MUTED }}>
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
};

export default ProfessionalForm;
